import { useState, useRef, useEffect, useCallback } from "react";
import { createPortal } from "react-dom";

export const ActionMenu = ({ actions = [] }) => {
  const [open, setOpen] = useState(false);
  const [menuPosition, setMenuPosition] = useState(null);
  const wrapperRef = useRef(null);
  const triggerRef = useRef(null);
  const menuRef = useRef(null);

  const updateMenuPosition = useCallback(() => {
    if (!triggerRef.current) return;

    const rect = triggerRef.current.getBoundingClientRect();
    setMenuPosition({
      top: rect.bottom + 6,
      right: Math.max(window.innerWidth - rect.right, 8),
    });
  }, []);

  useEffect(() => {
    const handleClickOutside = (e) => {
      const clickedTrigger = wrapperRef.current?.contains(e.target);
      const clickedMenu = menuRef.current?.contains(e.target);

      if (!clickedTrigger && !clickedMenu) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    if (!open) return undefined;

    updateMenuPosition();
    window.addEventListener("resize", updateMenuPosition);
    window.addEventListener("scroll", updateMenuPosition, true);

    return () => {
      window.removeEventListener("resize", updateMenuPosition);
      window.removeEventListener("scroll", updateMenuPosition, true);
    };
  }, [open, updateMenuPosition]);

  const handleTriggerClick = () => {
    const nextOpen = !open;
    if (nextOpen) updateMenuPosition();
    setOpen(nextOpen);
  };

  const menu =
    open && menuPosition
      ? createPortal(
          <div
            className="action-menu"
            ref={menuRef}
            style={{
              top: `${menuPosition.top}px`,
              right: `${menuPosition.right}px`,
            }}
          >
            {actions
              .filter((action) => action.onClick)
              .map((action, idx) => (
                <button
                  type="button"
                  key={idx}
                  className="action-menu-item"
                  onClick={() => {
                    action.onClick();
                    setOpen(false);
                  }}
                >
                  {action.icon && <action.icon className="action-item-icon" />}
                  <span className="action-item-label">{action.label}</span>
                </button>
              ))}
          </div>,
          document.body,
        )
      : null;

  return (
    <div className="action-menu-wrapper" ref={wrapperRef}>
      <button
        ref={triggerRef}
        className="action-menu-trigger"
        onClick={handleTriggerClick}
      >
        ⋮
      </button>

      {menu}
    </div>
  );
};
